<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-5">
          {{ $t('Operation Report') }}
        </h1>
      </v-col>
    </v-row>
    <v-card class="mx-auto pa-5 mb-5">
      <v-card-text>
        <h2 class="mb-5">
          Фильтр
        </h2>
        <v-row class="border_bottom">
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedPlatform"
              :items="platform"
              label="Платформа"
              item-text="name"
              item-value="id"
              outlined
              prepend-icon="mdi-home"
              hide-details
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedClient"
              :items="clientsFilterByPlatform"
              label="Клиент"
              item-text="legal_name"
              item-value="id"
              clearable
              outlined
              prepend-icon="mdi-account-outline"
              hide-details
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedSupplier"
              :items="suppliersFilter"
              label="Поставщик"
              item-text="legal_name"
              item-value="id"
              clearable
              outlined
              prepend-icon="mdi-account"
              hide-details
            />
          </v-col>
          <v-col cols="6" lg="3">
            <v-select
              v-model="selectedStatusApplication"
              :items="statusApplication"
              label="Статус"
              item-text="name"
              item-value="code"
              clearable
              outlined
              prepend-icon="mdi-check-network"
              hide-details
              multiple
            />
          </v-col>
        </v-row>
        <v-row>
          <v-btn
            v-if="filtersChanged()"
            class="clear-filters-btn ml-auto"
            @click="clearFilters"
          >
            Очистить фильтры
          </v-btn>
        </v-row>
        <v-row class="mt-10 align-end">
          <v-col
            cols="12"
            sm="6"
            lg="3"
          >
            <date-range
              :local-storage-prefix="localStoragePrefix"
              @datesChanged="dates = $event"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="4"
          >
            <v-text-field
              :value="reportFreshness"
              label="Актуальность отчёта"
              prepend-icon="mdi-clock-check-outline"
              outlined
              readonly
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="auto"
            class="d-flex align-center flex-wrap"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="d-inline-flex mt-2 mt-sm-0"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-btn
                    color="success"
                    :disabled="isDataLoading || operations.length === 0"
                    @click="downloadXLSX"
                  >
                    Отчёт в XLSX
                    <v-icon
                      right
                      dark
                    >
                      mdi-download
                    </v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                В файл попадает текущая выборка с сервера ({{ operations.length }} операций), те же данные, что в таблице ниже.
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row>
      <v-col v-if="operations.length" cols="12">
        <v-data-table
          ref="reportDataTable"
          item-key="id"
          :headers="operationTableHeaders"
          :items="operationTableRows"
          :custom-sort="operationReportCustomSort"
          :items-per-page="15"
          :page.sync="tablePage"
          :footer-props="{
            itemsPerPageOptions: [15, 25, 50, 100, 200],
            showFirstLastPage: true,
          }"
          class="elevation-1 operation-report-table"
          dense
          fixed-header
          height="560"
        >
          <template #item.dateIso="{ item }">
            {{ item.date }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import Component from 'vue-class-component';
import GetPlantsHRAndClientListing from '@/graphql/GetPlantsHRAndClientListing.gql';
import {
  Watch, Mixins, Prop,
} from 'vue-property-decorator';
import LoadingDataMixin from '@/mixins/LoadingDataMixin';
import XLSX from 'xlsx';
import DateRange from '@/components/common/DateRange.vue';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';
import { operationReport, reportStream } from '@/utils/config';
import { mapOperationToTableRow } from '@/utils/operationReportDisplay';
import { buildOperationReportWorkbook } from '@/utils/operationReportExcel';
import { parseReportResponse, delayMsUntilNextReportFetch } from '@/utils/reportResponse';
import formatReportFreshness from '@/utils/formatReportFreshness';

@Component({
  components: {
    DateRange,
  },
})
export default class OperationReport extends Mixins(LoadingDataMixin, EntityHelperMixin) {
  static MAX_XLSX_EXPORT_ROWS = 50000;

  operationTableHeaders = [
    {
      text: '№ Заявки',
      value: 'applicationId',
      sortable: true,
      width: '110px',
    },
    {
      text: 'Платформа',
      value: 'platform',
      sortable: true,
    },
    {
      text: 'Поставщик',
      value: 'supplier',
      sortable: true,
    },
    {
      text: 'Клиент',
      value: 'client',
      sortable: true,
    },
    {
      text: 'Дата',
      value: 'dateIso',
      sortable: true,
      width: '110px',
    },
    {
      text: 'Смена',
      value: 'shift',
      sortable: true,
      width: '90px',
    },
    {
      text: 'Тип операции',
      value: 'workType',
      sortable: true,
    },
    {
      text: 'Тип биллинга',
      value: 'billing',
      sortable: true,
    },
    {
      text: 'Объём',
      value: 'size',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Объём Факт',
      value: 'actualSize',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Люди',
      value: 'quantity',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Люди Факт',
      value: 'actualQuantity',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Время Факт',
      value: 'actualWorkTime',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Тариф операции',
      value: 'tariff',
      sortable: true,
      align: 'end',
    },
    {
      text: 'Кост-центр',
      value: 'costCenter',
      sortable: true,
    },
    {
      text: 'Статус',
      value: 'status',
      sortable: true,
    },
  ];

  @Prop({
    type: String,
    required: false,
    default: 'OperationReport',
  }) localStoragePrefix;

  operations = [];

  platform = [];

  clients = [];

  suppliers = [];

  statusApplication = [];

  selectedPlatform = this.getFromStorage('platform');

  defaultPlatform = null;

  selectedClient = this.getFromStorage('client');

  selectedSupplier = this.getFromStorage('supplier');

  selectedStatusApplication = this.getFromStorage('statusApplication')
    .split(',')
    .filter((s) => s.trim() !== '');

  isDataLoading = false;

  tablePage = 1;

  reportFreshness = '';

  dates = ['2020-11-10', '2020-11-27'];

  defaultDates = [];

  _loadDebounceTimer = null;

  _reportRefreshTimer = null;

  get operationTableRows() {
    return this.operations.map((op) => mapOperationToTableRow(op));
  }

  /**
   * Сортировка по числам и дате ISO; текст — localeCompare.
   * Поля _* задаются в mapOperationToTableRow (Talend-округление).
   */
  operationReportCustomSort(items, sortBy, sortDesc, locale) {
    if (!sortBy || !sortBy.length) return items;
    const key = sortBy[0];
    const desc = sortDesc[0];
    const factor = desc ? -1 : 1;
    const numericKey = {
      size: '_size',
      actualSize: '_actualSize',
      quantity: '_quantity',
      actualQuantity: '_actualQuantity',
      actualWorkTime: '_actualWorkTime',
      tariff: '_tariff',
    }[key];
    if (key === 'dateIso') {
      return [...items].sort((a, b) => {
        if (a.dateIso < b.dateIso) return -1 * factor;
        if (a.dateIso > b.dateIso) return 1 * factor;
        return 0;
      });
    }
    if (numericKey) {
      return [...items].sort((a, b) => {
        const av = a[numericKey];
        const bv = b[numericKey];
        const aNum = av === null || av === undefined ? Number.NEGATIVE_INFINITY : Number(av);
        const bNum = bv === null || bv === undefined ? Number.NEGATIVE_INFINITY : Number(bv);
        return factor * (aNum - bNum);
      });
    }
    return [...items].sort((a, b) => {
      const av = a[key] === null || a[key] === undefined ? '' : String(a[key]);
      const bv = b[key] === null || b[key] === undefined ? '' : String(b[key]);
      return factor * av.localeCompare(bv, locale || undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  calculateDefaultDates() {
    const dateNow = new Date();
    const dateAgo = new Date();
    dateAgo.setMonth(dateAgo.getMonth() - 1);
    dateAgo.setDate(1);
    this.defaultDates = [dateAgo.toISOString().split('T')[0], dateNow.toISOString().split('T')[0]];
  }

  get clientsFilterByPlatform() {
    if (this.selectedPlatform && Number(this.selectedPlatform)) {
      return this.clients.filter((i) => i.Plants && i.Plants.some((ele) => Number(ele.id) === Number(this.selectedPlatform)));
    }
    return this.clients;
  }

  get suppliersFilter() {
    if (this.selectedPlatform && Number(this.selectedPlatform)) {
      return this.suppliers.filter((i) => i.PlantsHR && i.PlantsHR.some((ele) => Number(ele.id) === Number(this.selectedPlatform)));
    }
    return this.suppliers;
  }

  downloadXLSX() {
    if (!Array.isArray(this.operations) || this.operations.length === 0) {
      this.$bus.$emit('notification', 'Нет данных для экспорта', 'warning');
      return null;
    }

    if (this.operations.length > OperationReport.MAX_XLSX_EXPORT_ROWS) {
      // Подтверждение крупного экспорта
      // eslint-disable-next-line no-alert
      const shouldContinue = window.confirm(
        `Вы выбрали ${this.operations.length} строк. Экспорт может занять много времени. Продолжить?`,
      );
      if (!shouldContinue) {
        return null;
      }
    }

    const wb = buildOperationReportWorkbook(this.operations);
    let fileName = `operation-report-${this.dates.join('-')}`;
    if (this.selectedPlatform) {
      fileName += `-platform_${this.platform.find((i) => i.id === this.selectedPlatform)?.name || ''}`;
    }
    if (this.selectedClient) {
      fileName += `-client_${this.clientsFilterByPlatform.find((i) => i.id === this.selectedClient)?.legal_name || ''}`;
    }
    if (this.selectedSupplier) {
      fileName += `-provider_${this.suppliersFilter.find((i) => i.id === this.selectedSupplier)?.legal_name || ''}`;
    }
    if (this.selectedStatusApplication.length) {
      const selectedStatus = this.selectedStatusApplication.map((status) => this.statusApplication.find((i) => i.code === status)?.name);
      fileName += `-status_${selectedStatus.join('-') || ''}`;
    }
    return XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

  async loadOperations() {
    this.isDataLoading = true;
    this.reportFreshness = '';
    if (!this.selectedPlatform) {
      this.selectedPlatform = this.defaultPlatform;
    }
    let recommendedNextFetchAt = null;
    try {
      const dates = Array.isArray(this.dates) ? this.dates : [];
      const statuses = Array.isArray(this.selectedStatusApplication)
        ? this.selectedStatusApplication
        : [];
      const params = {
        date_from: dates[0],
        date_to: dates[1],
        stream: reportStream.mode,
      };
      if (this.selectedPlatform) {
        params.platform_id = String(this.selectedPlatform);
      }
      if (this.selectedClient) {
        params.client_id = String(this.selectedClient);
      }
      if (this.selectedSupplier) {
        params.supplier_id = String(this.selectedSupplier);
      }
      if (statuses.length) {
        params.status_codes = statuses.join(',');
      }

      const { data: response } = await axios.get(operationReport.getData, {
        params,
        responseType: 'json',
      });
      const parsed = parseReportResponse(response);
      recommendedNextFetchAt = parsed.recommendedNextFetchAt || null;
      this.operations = parsed.operations;
      this.reportFreshness = formatReportFreshness(parsed.minProcessedAt || parsed.lastProcessedAt);
      if (parsed.error) {
        this.$bus.$emit('notification', parsed.error, 'warning');
      }
      this.isDataLoading = false;
    } catch (e) {
      console.error(e);
      this.operations = [];
      this.reportFreshness = '';
      this.isDataLoading = false;
    } finally {
      this.scheduleNextReportFetch(recommendedNextFetchAt);
    }
  }

  scheduleNextReportFetch(recommendedNextFetchAt) {
    if (this._isDestroyed) {
      return;
    }
    clearTimeout(this._reportRefreshTimer);
    const delayMs = delayMsUntilNextReportFetch(recommendedNextFetchAt);
    this._reportRefreshTimer = setTimeout(() => {
      if (this._isDestroyed) {
        return;
      }
      this.loadOperations();
    }, delayMs);
  }

  scheduleLoadOperations() {
    // Таблица сама отступает лишь на одну страницу: с дальней страницы короткая выборка рисуется пустой.
    this.tablePage = 1;
    clearTimeout(this._loadDebounceTimer);
    this._loadDebounceTimer = setTimeout(() => this.loadOperations(), 50);
  }

  loadPlatformAndClients() {
    return this.$apollo.query({
      query: GetPlantsHRAndClientListing,
      variables: {},
    }).then((res) => {
      if (res?.data?.getPlantsHRListing?.edges?.length) {
        this.platform = res?.data?.getPlantsHRListing?.edges.map((item) => item.node);
        this.defaultPlatform = this.platform[0]?.id;
      }
      if (res?.data?.getClientListing?.edges?.length) {
        this.clients = res?.data?.getClientListing?.edges.map((item) => item.node);
      }
      if (res?.data?.getSupplierListing?.edges?.length) {
        this.suppliers = res?.data?.getSupplierListing?.edges.map((item) => item.node);
      }
      if (res?.data?.getStatusApplicationListing?.edges?.length) {
        this.statusApplication = res?.data?.getStatusApplicationListing?.edges.map((item) => item.node);
      }
      return res;
    }).catch((e) => {
      console.error(e);
    });
  }

  created() {
    const query = this.$route?.query || {};
    if (query.platform) this.selectedPlatform = query.platform;
    if (query.client) this.selectedClient = query.client;
    if (query.supplier) this.selectedSupplier = query.supplier;
    if (query.statuses) {
      this.selectedStatusApplication = String(query.statuses).split(',').filter((s) => s.trim() !== '');
    }
  }

  async beforeMount() {
    this.calculateDefaultDates();
    await this.loadPlatformAndClients();
    const dates = Array.isArray(this.dates) ? this.dates : [];
    if (dates.length === 2) {
      await this.loadOperations();
    }
  }

  beforeDestroy() {
    clearTimeout(this._reportRefreshTimer);
    clearTimeout(this._loadDebounceTimer);
  }

  syncQueryParams() {
    const query = { ...this.$route.query };
    if (this.selectedPlatform) { query.platform = String(this.selectedPlatform); } else { delete query.platform; }
    if (this.selectedClient) { query.client = String(this.selectedClient); } else { delete query.client; }
    if (this.selectedSupplier) { query.supplier = String(this.selectedSupplier); } else { delete query.supplier; }
    const statuses = Array.isArray(this.selectedStatusApplication) ? this.selectedStatusApplication : [];
    if (statuses.length) { query.statuses = statuses.join(','); } else { delete query.statuses; }
    delete query.value;
    const current = JSON.stringify(this.$route.query);
    if (current !== JSON.stringify(query)) {
      this.$router.replace({ query }).catch(() => undefined);
    }
  }

  @Watch('selectedPlatform')
  selectedPlatformChanged(value) {
    this.selectedClient = null;
    this.selectedSupplier = null;
    if (typeof window !== 'undefined' && localStorage) {
      this.setToStorage('platform', value);
      this.setToStorage('client', '');
      this.setToStorage('supplier', '');
    }
    this.syncQueryParams();
    this.scheduleLoadOperations();
  }

  @Watch('selectedClient')
  selectedClientChanged(value) {
    if (typeof window !== 'undefined' && localStorage) {
      this.setToStorage('client', value);
    }
    this.syncQueryParams();
    this.scheduleLoadOperations();
  }

  @Watch('selectedSupplier')
  selectedSupplierChanged(value) {
    if (typeof window !== 'undefined' && localStorage) {
      this.setToStorage('supplier', value);
    }
    this.syncQueryParams();
    this.scheduleLoadOperations();
  }

  @Watch('selectedStatusApplication')
  selectedStatusApplicationChanged(value) {
    if (typeof window !== 'undefined' && localStorage) {
      this.setToStorage('statusApplication', (Array.isArray(value) ? value : []).join(','));
    }
    this.syncQueryParams();
    this.scheduleLoadOperations();
  }

  @Watch('dates')
  datesChanged() {
    this.syncQueryParams();
    this.scheduleLoadOperations();
  }

  filtersChanged() {
    const statuses = Array.isArray(this.selectedStatusApplication) ? this.selectedStatusApplication : [];
    const dates = Array.isArray(this.dates) ? this.dates : [];
    const defaultDates = Array.isArray(this.defaultDates) ? this.defaultDates : [];
    return this.selectedPlatform !== this.defaultPlatform
      || this.selectedSupplier
      || this.selectedClient
      || statuses.length > 0
      || dates.length !== 2
      || dates[0] !== defaultDates[0]
      || dates[1] !== defaultDates[1];
  }

  clearFilters() {
    this.selectedStatusApplication = [];
    if (this.selectedPlatform === this.defaultPlatform) {
      this.selectedPlatformChanged(this.selectedPlatform);
    } else {
      this.selectedPlatform = this.defaultPlatform;
    }
    this.$bus.$emit('clear-filters');
  }
}
</script>

<style scoped>
  .operation-report-table /deep/ table th {
    white-space: nowrap;
  }
</style>
